<p-dialog
  [(visible)]="visible"
  [draggable]="false"
  [resizable]="false"
  [modal]="true"
  [closable]="true"
  [header]="dialogTitle"
  [appendTo]="'body'"
  [styleClass]="'discount-dialog'"
>
  <ng-template pTemplate="header">
    <div class="dialog-header" [attr.data-subtitle]="isEditing ? ('discountsCrud.dialogSubtitle.edit' | transloco) : ('discountsCrud.dialogSubtitle.new' | transloco)">
      <span class="dialog-title">{{ dialogTitle }}</span>
    </div>
  </ng-template>

  <form [formGroup]="discountForm" (ngSubmit)="onSubmit()" (keydown.enter)="submitOnEnterSafe($event)" class="form-container">
    <div class="form-group">
      <!-- What: name, type and value -->
      <section class="form-section">
        <h4 class="section-title">{{ 'discountsCrud.sections.discount' | transloco }}</h4>

        <div class="form-field">
          <app-input
            [label]="'discountsCrud.fields.name' | transloco"
            [placeholder]="'discountsCrud.fields.namePlaceholder' | transloco"
            formControlName="name"
            [required]="true"
            [error]="!!discountForm.get('name')?.invalid && !!discountForm.get('name')?.touched"
            [errorMessage]="getErrorMessage('name') || ''"
            size="medium"
          ></app-input>
        </div>

        <!-- Type as a two-option segmented control: % or € -->
        <div class="form-field">
          <span class="field-label" id="discount-type-label">{{ 'discountsCrud.fields.type' | transloco }}</span>
          <div class="type-segment" role="radiogroup" aria-labelledby="discount-type-label">
            @for (opt of typeOptions; track opt.value) {
              <button
                type="button"
                class="type-option"
                role="radio"
                [attr.aria-checked]="discountForm.get('type')?.value === opt.value"
                [class.active]="discountForm.get('type')?.value === opt.value"
                (click)="discountForm.get('type')?.setValue(opt.value); discountForm.get('type')?.markAsDirty()"
              >
                <lucide-icon [name]="opt.value === 'PERCENTAGE' ? 'Percent' : 'Euro'" [size]="16"></lucide-icon>
                {{ opt.label }}
              </button>
            }
          </div>
        </div>

        <div class="form-field">
          <app-input
            [label]="'discountsCrud.fields.value' | transloco"
            type="number"
            formControlName="value"
            [required]="true"
            [placeholder]="discountForm.get('type')?.value === 'PERCENTAGE' ? ('discountsCrud.fields.valuePlaceholderPercent' | transloco) : ('discountsCrud.fields.valuePlaceholderFixed' | transloco)"
            [error]="!!discountForm.get('value')?.invalid && !!discountForm.get('value')?.touched"
            [errorMessage]="getErrorMessage('value') || ''"
            size="medium"
          ></app-input>
        </div>
      </section>

      <!-- Who: a user (optional) and/or a promo code -->
      <section class="form-section">
        <h4 class="section-title">{{ 'discountsCrud.sections.who' | transloco }}</h4>
        <div class="form-row">
          <div class="form-field">
            <app-select
              [label]="'discountsCrud.fields.user' | transloco"
              formControlName="userId"
              [options]="userOptions"
              [filter]="true"
              [filterPlaceholder]="'discountsCrud.fields.userFilterPlaceholder' | transloco"
              [loading]="usersLoading"
              (filterChange)="onUserSearch($event)"
              [placeholder]="'discountsCrud.fields.userPlaceholder' | transloco"
              [appendTo]="null"
              size="medium"
              [error]="usersLoadError"
              [errorMessage]="'discountsCrud.messages.usersLoadError' | transloco"
            ></app-select>
          </div>
          <div class="form-field">
            <app-input
              [label]="'discountsCrud.fields.promoCode' | transloco"
              [placeholder]="'discountsCrud.fields.promoCodePlaceholder' | transloco"
              formControlName="code"
              [error]="!!discountForm.get('code')?.invalid && !!discountForm.get('code')?.touched"
              [errorMessage]="getErrorMessage('code') || ''"
              size="medium"
            ></app-input>
          </div>
        </div>
      </section>

      <!-- When: start and end (stacked on a phone: a date + its icon doesn't fit in half) -->
      <section class="form-section">
        <h4 class="section-title">{{ 'discountsCrud.sections.validity' | transloco }}</h4>
        <div class="form-row">
          <div class="form-field">
            <app-datepicker
              [label]="'discountsCrud.fields.startDate' | transloco"
              [placeholder]="'discountsCrud.fields.datePlaceholder' | transloco"
              formControlName="startDate"
              [appendTo]="null"
            ></app-datepicker>
          </div>
          <div class="form-field">
            <app-datepicker
              [label]="'discountsCrud.fields.endDate' | transloco"
              [placeholder]="'discountsCrud.fields.datePlaceholder' | transloco"
              formControlName="endDate"
              [error]="!!discountForm.get('endDate')?.invalid && !!discountForm.get('endDate')?.touched"
              [errorMessage]="getErrorMessage('endDate') || ''"
              [appendTo]="null"
            ></app-datepicker>
          </div>
        </div>
      </section>

      <!-- Limits (both optional) -->
      <section class="form-section">
        <h4 class="section-title">{{ 'discountsCrud.sections.limits' | transloco }}</h4>
        <div class="form-row form-row--pair">
          <div class="form-field">
            <app-input
              [label]="'discountsCrud.fields.maxUses' | transloco"
              type="number"
              formControlName="maxUses"
              [placeholder]="'discountsCrud.fields.maxUsesPlaceholder' | transloco"
              [error]="!!discountForm.get('maxUses')?.invalid && !!discountForm.get('maxUses')?.touched"
              [errorMessage]="getErrorMessage('maxUses') || ''"
              size="medium"
            ></app-input>
          </div>
          <div class="form-field">
            <app-input
              [label]="'discountsCrud.fields.minOrderAmount' | transloco"
              type="number"
              formControlName="minOrderAmount"
              [placeholder]="'discountsCrud.fields.minOrderAmountPlaceholder' | transloco"
              [error]="!!discountForm.get('minOrderAmount')?.invalid && !!discountForm.get('minOrderAmount')?.touched"
              [errorMessage]="getErrorMessage('minOrderAmount') || ''"
              size="medium"
            ></app-input>
          </div>
        </div>
      </section>
    </div>

    <div class="form-actions">
      <app-button
        [label]="actionButtonLabel"
        type="submit"
        size="medium"
        [disabled]="isLoading"
        [loading]="isLoading"
        class="submit-button"
      ></app-button>
    </div>
  </form>
</p-dialog>
